import { ParamMap, Router } from '@angular/router';
import { QueryParams } from 'src/app/models/navigation/navigationParams.interface';
import { getConfig } from './side-panel-config';
import { SidePanel, SidePanelRouteState, SidePanelTree, SidePanelViewModel } from './types/side-panel';

export type SidePanelWithQueryParams = SidePanel<unknown> & { dependsOn: Record<QueryParams, string> };

export class SidePanelQueryParamsState {
  private readonly observedParams: QueryParams[] = [
    QueryParams.integration,
    QueryParams.violation,
    QueryParams.resource,
    QueryParams.agentStep,
    QueryParams.finding,
    QueryParams.issue,
    QueryParams.stepThreadId,
    QueryParams.prevention,
  ];
  private routeState: SidePanelRouteState = this.observedParams.reduce(
    (result, current) => ({ ...result, [current]: null }),
    {} as SidePanelRouteState
  );
  private readonly emptyRouteState: SidePanelRouteState = this.observedParams.reduce(
    (result, current) => ({ ...result, [current]: null }),
    {} as SidePanelRouteState
  );
  // There should be no conflicting routes - first route will be preferred
  private readonly sidePanelTree: SidePanelTree[] = getConfig();

  private _viewModel: SidePanelViewModel | undefined;

  public get viewModel() {
    return this._viewModel;
  }

  public getSidePanelsToOpen(): SidePanelWithQueryParams[] {
    return this.parseTree();
  }

  public getRouteState(): SidePanelRouteState {
    return structuredClone(this.routeState);
  }

  public getEmptyRouteState(): SidePanelRouteState {
    return structuredClone(this.emptyRouteState);
  }

  public update(router: Router, params: ParamMap) {
    this.routeState = this.observedParams.reduce(
      (result, current) => ({
        ...result,
        [current]: params.get(current),
      }),
      {} as SidePanelRouteState
    );

    this._viewModel = router.getCurrentNavigation()?.extras?.state?.['viewModel'];
  }

  private parseTree() {
    const sidePanels: SidePanelWithQueryParams[] = [];
    let branchParsedTillEnd = false;
    for (let i = 0; i < this.sidePanelTree.length; i++) {
      if (branchParsedTillEnd) {
        continue;
      }
      const stack = [this.sidePanelTree[i]];
      let paramsSinceLastSidePanel = {} as Record<QueryParams, string>;

      while (stack.length) {
        const leaf = stack.pop();

        if (!leaf || !this.routeState[leaf.param]) {
          continue;
        }

        paramsSinceLastSidePanel[leaf.param] = this.routeState[leaf.param] as string;

        if (leaf.config) {
          sidePanels.push({ ...leaf.config, dependsOn: paramsSinceLastSidePanel });
          paramsSinceLastSidePanel = {} as Record<QueryParams, string>;
        }

        if (leaf.nested) {
          stack.push(leaf.nested);
        }

        if (leaf.nested === null) {
          branchParsedTillEnd = true;
        }
      }
    }

    return sidePanels;
  }
}
